import type { KeyboardLayout } from "@/domain/types";
import type { ZoneCode } from "@/domain/zones";
import { charToCodes } from "@/hardware/keyboard-layouts/index";

/**
 * The token vocabulary and its resolution. A Profile is character-space, so it binds named
 * tokens (a char, a positional key, or a mouse zone) that resolve to physical positions through
 * the active Keyboard Layout. The resolution machinery is game-agnostic: the projection engine,
 * TheCore generation, and SC2 ingestion all resolve tokens here. The named-token tables below are
 * SC2's serialization spellings, the only named-token vocabulary in use today (Destiny binds literal
 * chars); a second game with a conflicting vocabulary is when these tables would split back out.
 */

/**
 * Named tokens that denote a character. The Profile is character-space,
 * so these resolve to a code through the active Keyboard Layout.
 */
export const TOKEN_TO_CHAR: Record<string, string> = {
  SemiColon: ";",
  Apostrophe: "'",
  BracketOpen: "[",
  BracketClose: "]",
  Minus: "-",
  Equals: "=",
  Slash: "/",
  Period: ".",
  Comma: ",",
  BackSlash: "\\",
  Grave: "`",
};

/** Named tokens for keys that emit no character; positional, identical in every Keyboard Layout. */
export const TOKEN_TO_CODE: Record<string, string> = {
  Backspace: "Backspace",
  Tab: "Tab",
  CapsLock: "CapsLock",
  Enter: "Enter",
  Escape: "Escape",
  Space: "Space",
  Delete: "Delete",
  Insert: "Insert",
  Home: "Home",
  End: "End",
  PageUp: "PageUp",
  PageDown: "PageDown",
  // SC2's numpad token space; positional, and never produced by character tokens
  NumPad0: "Numpad0",
  NumPad1: "Numpad1",
  NumPad2: "Numpad2",
  NumPad3: "Numpad3",
  NumPad4: "Numpad4",
  NumPad5: "Numpad5",
  NumPad6: "Numpad6",
  NumPad7: "Numpad7",
  NumPad8: "Numpad8",
  NumPad9: "Numpad9",
  NumPadPlus: "NumpadAdd",
  NumPadMinus: "NumpadSubtract",
  NumPadMultiply: "NumpadMultiply",
  NumPadDivide: "NumpadDivide",
  NumPadDecimal: "NumpadDecimal",
  NumPadEnter: "NumpadEnter",
};

/**
 * SC2's mouse tokens, a namespace closed at five: Blizzard's game strings define
 * exactly these Mouse/ entries, and no wheel token exists (the game hardcodes wheel
 * scroll to zoom). Positional like TOKEN_TO_CODE but resolving to zone codes.
 * Back is button 4 and forward is button 5, the XBUTTON1/2 convention.
 */
export const MOUSE_TOKEN_TO_ZONE: Record<string, ZoneCode> = {
  LeftMouseButton: "MouseLeft",
  RightMouseButton: "MouseRight",
  MiddleMouseButton: "MouseMiddle",
  BackMouseButton: "Mouse4",
  ForwardMouseButton: "Mouse5",
};

const CHAR_TO_TOKEN: Record<string, string> = Object.fromEntries(
  Object.entries(TOKEN_TO_CHAR).map(([token, ch]) => [ch, token]),
);

const ZONE_TO_TOKEN: Record<string, string> = Object.fromEntries(
  Object.entries(MOUSE_TOKEN_TO_ZONE).map(([token, zone]) => [zone, token]),
);

const CODE_TO_TOKEN: Record<string, string> = Object.fromEntries(
  Object.entries(TOKEN_TO_CODE).map(([token, code]) => [code, token]),
);

const FKEY = /^F([1-9]|1[0-2])$/;

const tokenReverseCache = new WeakMap<Record<string, string>, Record<string, string>>();

/**
 * The code a recovered serialization-token table names: the inverse of a
 * KeyboardLayout.tokens map, so a national OEM token (AZERTY's OEM8, BracketOpen, ...) parses back to
 * the physical key it serializes - which charToCodes cannot, those tokens denoting no character. The
 * shipped tables are injective; first-seen wins defensively. Cached by the tokens object's identity,
 * so a derived board layer reusing a layout's tokens shares the universal path's reverse table.
 */
export function codeForLayoutToken(
  tokens: Record<string, string>,
  token: string,
): string | undefined {
  let rev = tokenReverseCache.get(tokens);
  if (!rev) {
    rev = {};
    for (const [code, tok] of Object.entries(tokens)) if (!(tok in rev)) rev[tok] = code;
    tokenReverseCache.set(tokens, rev);
  }
  return rev[token];
}

function layoutTokenCode(layout: KeyboardLayout, token: string): string | undefined {
  return layout.tokens ? codeForLayoutToken(layout.tokens, token) : undefined;
}

/**
 * The canonical single character a bare token denotes, NFC-normalized: an ASCII
 * letter folds to uppercase (the legend convention), every other length-1 char
 * (digit, accented letter, punctuation) passes through verbatim; multi-character
 * and empty strings reject. NFC, not NFKC: superscript two must stay distinct from
 * the digit "2". Shared by the codec's two gates and the sibling-file aligner
 * (derive-layout) so all three resolve single chars by one rule.
 */
export function bareSingleChar(s: string): string | null {
  const c = s.normalize("NFC");
  if (/^[a-z]$/i.test(c)) return c.toUpperCase();
  if (c.length === 1) return c;
  return null;
}

/** The character a char-space token denotes, or null for positional and unknown tokens. */
export function tokenChar(token: string): string | null {
  if (TOKEN_TO_CHAR[token]) return TOKEN_TO_CHAR[token];
  return bareSingleChar(token);
}

/** The file token for a character (serialize direction), or null when no token exists. */
export function charToken(ch: string): string | null {
  if (CHAR_TO_TOKEN[ch]) return CHAR_TO_TOKEN[ch];
  return bareSingleChar(ch);
}

/** True when the token names a physical position (key or zone) directly, carrying no character information. */
export function isPositionalToken(token: string): boolean {
  return TOKEN_TO_CODE[token] != null || MOUSE_TOKEN_TO_ZONE[token] != null || FKEY.test(token);
}

/** The file token for a positional (non-character) code or zone code, or null. */
export function codeToken(code: string): string | null {
  if (CODE_TO_TOKEN[code]) return CODE_TO_TOKEN[code];
  if (ZONE_TO_TOKEN[code]) return ZONE_TO_TOKEN[code];
  if (FKEY.test(code)) return code;
  return null;
}

/**
 * Resolve one key token to every bindable position it lands on through a
 * Keyboard Layout: KeyboardEvent.codes for keys, zone codes for mouse buttons.
 * Positional tokens (Enter, F1..F12, the five mouse tokens) bypass the layout.
 * Character tokens land on EVERY key emitting the char: a board layer can repeat a
 * keycode across physical keys (a gaming layer putting KC_8 on a macro key beside
 * the number row), and in game every emitter fires the hotkey. Numpad-coded keys
 * are excluded: SC2 reserves NumPad tokens for those, so a character token never
 * fires on one. A digit token the layout cannot map falls back to its digit-row key:
 * AZERTY's digit row emits its unshifted chars, so "2" has no unshifted home there,
 * and dropping control groups is the worst failure a visualizer can have. Everything
 * else unmappable (bare modifiers, F13 and up) resolves to no positions.
 */
export function tokenToCodes(token: string, layout: KeyboardLayout): string[] {
  const positional = TOKEN_TO_CODE[token] ?? MOUSE_TOKEN_TO_ZONE[token];
  if (positional) return [positional];
  if (FKEY.test(token)) return [token];
  // A layout's own serialization token names its physical key directly (AZERTY writes the US-"]"
  // key as "SemiColon", the "!" key as "OEM8"); this wins over the char reading of the same string.
  const viaTable = layoutTokenCode(layout, token);
  if (viaTable) return [viaTable];
  // An overflow-target token names the key the ISO Enter merge VACATED: the tokens table
  // redirects that key's serialization onto Enter (TheCore's convention), so its true VK token
  // (kbdlayout.info: VK_OEM_7 on UK, VK_OEM_8 on Swiss - both scancode 2B) has no table entry
  // of its own and is recovered here. This wins over the char reading too: on UK the char path
  // would land "Apostrophe" on the Quote key, whose VK is Grave.
  if (layout.overflow) {
    for (const [contested, target] of Object.entries(layout.overflow)) {
      if (token !== target) continue;
      const vacated = Object.entries(layout.tokens ?? {})
        .filter(([, t]) => t === contested)
        .map(([code]) => code);
      if (vacated.length > 0) return vacated;
    }
  }
  const ch = tokenChar(token);
  if (ch == null) return [];
  const codes = charToCodes(layout, ch).filter((c) => !c.startsWith("Numpad"));
  if (codes.length > 0) return codes;
  if (/^[0-9]$/.test(ch)) return [`Digit${ch}`];
  return [];
}
